import "./index.less";
import React, { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { Form, Button } from "antd";
import classnames from "classnames";
import TimePicker from "./TimeRangePicker";
import WeightRangeField from "./WeightRangeField";
import PaymentRangeField from "./PaymentRangeField";
import ItemQuantityRangeField from "./ItemQuantityRangeField";
import BuyerRemarksField from "./RemarksField";
import PrintStatusField from "./PrintStatusField";
import LogisticsInfoStatusField from "./LogisticsInfoStatusField";
import QuickField from "./QuickField";
import BuyerUsernameField from "./BuyerUsernameField";
import OriginalIdField from "./OriginalIdField";
import SellerFlagField from "./SellerFlagField";
import WaybillNoField from "./WaybillNoField";
import ConsigneeNameField from "./ConsigneeNameField";
import PhoneField from "./PhoneField";
import LogisticsCompanyField from "./LogisticsCompanyField";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { useGlobalStore } from "@/store/hooks";
import ShippingAreaSelect from "./ShippingAreaSelect";
import StockNo from "./StockNo";
import OrderMarkField from "./OrderMarkField";
import OptionButton from "./OptionButton";
import { SearchContext } from "./context";
import ShipperField from "./ShipperField";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";
import RetailerField from "./RetailerField";
import UndeliverableAreaField from "./UndeliverableAreaField";
import FulfillmentType from "@/enum/FulfillmentType";
import SupplierField from "./SupplierField";
import AffiliateMarketerField from "./AffiliateMarketerField";
import ShopType from "@/enum/ShopType";
import FrequentlyUsedOrderSearchPanel from "./FrequentlyUsedOrderSearchPanel";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { useModalStore } from "@/hooks/Modal/hooks";
import OrderSearchPaneSettingModal from "./OrderSearchPaneSettingModal";
import ProductOriginalIdField from "./LinkedProductSearchFields/ProductOriginalIdField";
import FrequentlyUsedField from "@/enum/FrequentlyUsedField";
import ConsigneeAreaField from "./ConsigneeAreaField";
import ConsigneeStreetField from "./ConsigneeStreetField";
import SellerRemarksField from "./SellerRemarksField";
import OrderTagSelectField from "./OrderTagSelectField";
import TimeType from "@/scenes/OrderManagement/enum/TimeType";
import { isEmpty } from "@/utils";
import { useHistory, useLocation } from "react-router";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import { collectEvent } from "@/services/dataTrack";
import AppVersionNameMap from "@/map/AppVersionNameMap";
import SkuOriginalIdField from "./SkuOriginalIdField";
import OrderItemExternalIdField from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/OrderItemExternalIdField";
import OrderItemTitleField from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/OrderItemTitleField";
import SkuPropertiesField from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/SkuPropertiesField";
import LinkedProductSearchFieldsStore, {
    LinkedProductSearchFieldsStoreContext,
    useAutoHandleHotProductData,
} from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/LinkedProductSearchFieldsStore";
import { affiliateMarketerShopTypes, ShippingDeadlineShopTypes, SupplierShopTypes } from "@/utils/ShopUtils";
import OrderTagUnSelectField from "./OrderTagUnSelectField";
import SearchButton from "./SearchButton";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import OrderSortNameMap from "@/map/OrderSortNameMap";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import AbnormalOrderRecordTypeField from "./AbnormalOrderRecordTypeField";
import LogisticsStagnantHoursField from "./LogisticsStagnantHoursField";
import RemainingShippingHoursField from "./RemainingShippingHoursField";
import PrintBatchNoField from "./PrintBatchNoField";
import ItemStatusesField from "./ItemStatusesField";
import HiddenFields from "./HiddenFields";
import OrderSearchForm from "./OrderSearchForm";

interface SearchPanelProps {
    shopId: number;

    tab: OrderTab;
}

const SearchPanel: React.FC<SearchPanelProps> = ({ shopId, tab }) => {
    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();
    const [form] = Form.useForm<TableSearchState>();

    const [linkedProductSearchFieldsStore] = useState(() => new LinkedProductSearchFieldsStore(globalStore));

    useAutoHandleHotProductData({
        shopId,
        tab,
        linkedProductSearchFieldsStore,
        form,
    });

    const fulfillmentType = orderManagementStore.table?.listOptions?.fulfillmentType;
    const { isShowMoreOptions } = orderManagementStore.table;
    const initialValues: TableSearchState = { timeType: TimeType.ORDER };
    useEffect(() => {
        orderManagementStore.table.injectFormInstance(form);
    }, []);

    const location = useLocation();
    const history = useHistory();

    const { openModal } = useModalStore();

    const { loadOrderSearchPanelSettingList, orderSearchPanelLoading, frequentlyUsedFields, unFrequentlyUsedFields } =
        orderManagementStore.storage;

    useEffect(() => {
        loadOrderSearchPanelSettingList();
    }, []);

    const formCls = classnames("list-inline search-options-group", {
        "inline-flex": [OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab),
    });

    const buttonGroupCls = classnames(
        "mr50",
        [OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab)
            ? "inline-flex"
            : "flex justify-content-flex-end",
    );

    const shop = globalStore.shopMapping.get(shopId);

    const handleSearch = () => {
        try {
            const shopSetting = globalStore.shopSettingMapping.get(shopId);

            collectEvent(DataTrackEvent.SEARCH_ONE_SHOP_ORDER_LIST, {
                app_version: AppVersionNameMap[shopSetting?.appVersion],
                page_size: orderManagementStore.table.pagination.pageSize,
                order_sort: OrderSortNameMap[orderManagementStore.table.listOptions?.orderSort],
                shop_id: shopId,
                shop_name: shopSetting?.name,
                shop_type: ShopTypeNameMap[shopSetting?.type],
            });
        } catch (error) {}

        orderManagementStore.table.changePageNo(1);
        orderManagementStore.fetchOrders(shopId, tab);
    };

    const handleReset = () => {
        form.resetFields();

        orderManagementStore.table.switchShippingDeadlineChecked(false);
        orderManagementStore.table.switchShippingOverdueChecked(false);
        orderManagementStore.table.setActiveCriteriaId(null);
        handleSearch();
    };

    useEffect(() => {
        form.resetFields();

        if (isShowMoreOptions) {
            orderManagementStore.table.toggleMoreSearchOptions();
        }
    }, [shopId, tab]);

    useEffect(() => {
        return () => {
            form.resetFields();
        };
    }, []);

    /** 首页跳转过来 */
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const quick = searchParams.get("quick") as QuickEnum;

        if (isEmpty(quick)) return;

        searchParams.delete("quick");

        history.replace(location.pathname + "?" + searchParams.toString());
        form.setFieldsValue({ quicks: [quick] });
        orderManagementStore.table.switchShippingDeadlineChecked(true);
    }, []);

    if (tab === OrderTab.ABNORMAL) {
        return (
            <div className="order-search-pane">
                <div style={{ maxWidth: "1290px" }}>
                    <OrderSearchForm layout="inline" size="small" form={form} className={formCls} colon={false}>
                        <SearchContext.Provider value={{ onPressEnter: handleSearch }}>
                            <OriginalIdField shop={shop} />
                            <LogisticsInfoStatusField />
                            <AbnormalOrderRecordTypeField />
                        </SearchContext.Provider>
                    </OrderSearchForm>
                    <div className={buttonGroupCls}>
                        <div>
                            <SearchButton onSearch={handleSearch} />

                            <Button onClick={handleReset} size="small" className="mr10">
                                重置
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 如果是预发货
    if (tab === OrderTab.PRE_SHIP) {
        return (
            <div className="order-search-pane">
                <div style={{ maxWidth: "1290px" }}>
                    <OrderSearchForm
                        layout="inline"
                        size="small"
                        form={form}
                        className={formCls}
                        initialValues={initialValues}
                        colon={false}
                    >
                        <LinkedProductSearchFieldsStoreContext.Provider value={linkedProductSearchFieldsStore}>
                            <SearchContext.Provider value={{ onPressEnter: handleSearch }}>
                                <LogisticsInfoStatusField />
                                <LogisticsCompanyField />
                                <OriginalIdField shop={shop} />
                                <WaybillNoField />
                                <PhoneField shop={shop} />
                                <OrderItemTitleField tab={tab} shopId={shopId} />
                                <SkuPropertiesField shopId={shopId} />
                                <OrderItemExternalIdField shopId={shopId} />
                            </SearchContext.Provider>
                        </LinkedProductSearchFieldsStoreContext.Provider>
                    </OrderSearchForm>
                    <div className={buttonGroupCls}>
                        <div>
                            <SearchButton onSearch={handleSearch} />

                            <Button onClick={handleReset} size="small" className="mr10">
                                重置
                            </Button>

                            <OptionButton isShowMoreOptions={isShowMoreOptions} tab={tab} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function buildFields(fields: FrequentlyUsedField[]) {
        return fields.map((field) => {
            switch (field) {
                case FrequentlyUsedField.AFFILIATE_MARKETER:
                    if (affiliateMarketerShopTypes.includes(shop.type))
                        return (
                            <AffiliateMarketerField
                                key={FrequentlyUsedField.AFFILIATE_MARKETER}
                                form={form}
                                shops={[shop]}
                            />
                        );

                    return null;

                case FrequentlyUsedField.BUYER_REMARKS:
                    if (
                        ShopType.PDD_FDS === shop.type ||
                        ShopType.PDD_FULFILLMENT === shop.type ||
                        ShopType.ALIBABA_SUPPLIER === shop.type
                    )
                        return null;

                    return <BuyerRemarksField key={FrequentlyUsedField.BUYER_REMARKS} />;

                case FrequentlyUsedField.BUYER_USERNAME:
                    if (!SupplierShopTypes.includes(shop.type)) {
                        return <BuyerUsernameField shop={shop} key={FrequentlyUsedField.BUYER_USERNAME} />;
                    }

                    return null;

                case FrequentlyUsedField.CONSIGNEE_AREA:
                    return <ConsigneeAreaField form={form} key={FrequentlyUsedField.CONSIGNEE_AREA} />;

                case FrequentlyUsedField.CONSIGNEE_NAME:
                    return <ConsigneeNameField shop={shop} key={FrequentlyUsedField.CONSIGNEE_NAME} />;

                case FrequentlyUsedField.CONSIGNEE_PHONE:
                    return <PhoneField shop={shop} key={FrequentlyUsedField.CONSIGNEE_PHONE} />;

                case FrequentlyUsedField.CONSIGNEE_STREET:
                    return <ConsigneeStreetField key={FrequentlyUsedField.CONSIGNEE_STREET} shopType={shop.type} />;
                case FrequentlyUsedField.LOGISTICS_COMPANY:
                    return <LogisticsCompanyField key={FrequentlyUsedField.LOGISTICS_COMPANY} />;
                case FrequentlyUsedField.LOGISTICS_INFO_STATUS:
                    return <LogisticsInfoStatusField key={FrequentlyUsedField.LOGISTICS_INFO_STATUS} />;
                case FrequentlyUsedField.LOGISTICS_STAGNANT_HOURS:
                    return <LogisticsStagnantHoursField key={FrequentlyUsedField.LOGISTICS_STAGNANT_HOURS} />;
                case FrequentlyUsedField.REMAINING_SHIPPING_HOURS:
                    if (ShippingDeadlineShopTypes.includes(shop.type)) {
                        return <RemainingShippingHoursField key={FrequentlyUsedField.REMAINING_SHIPPING_HOURS} />;
                    }

                    return null;
                case FrequentlyUsedField.ORDER_ITEM_EXTERNAL_ID:
                    return (
                        <OrderItemExternalIdField key={FrequentlyUsedField.ORDER_ITEM_EXTERNAL_ID} shopId={shopId} />
                    );
                case FrequentlyUsedField.ORDER_ITEM_TITLE:
                    return <OrderItemTitleField key={FrequentlyUsedField.ORDER_ITEM_TITLE} tab={tab} shopId={shopId} />;
                case FrequentlyUsedField.ORDER_MARK:
                    return <OrderMarkField key={FrequentlyUsedField.ORDER_MARK} />;
                case FrequentlyUsedField.ORDER_ORIGINAL_ID:
                    return <OriginalIdField key={FrequentlyUsedField.ORDER_ORIGINAL_ID} />;
                case FrequentlyUsedField.ORDER_TIME_RANGE:
                    return <TimePicker shop={shop} key={FrequentlyUsedField.ORDER_TIME_RANGE} />;
                case FrequentlyUsedField.PAYMENT_AMOUNT_RANGE:
                    return <PaymentRangeField shop={shop} key={FrequentlyUsedField.PAYMENT_AMOUNT_RANGE} />;
                case FrequentlyUsedField.PRINT_STATUS:
                    return <PrintStatusField key={FrequentlyUsedField.PRINT_STATUS} />;
                case FrequentlyUsedField.PRODUCT_ORIGINAL_ID:
                    return <ProductOriginalIdField key={FrequentlyUsedField.PRODUCT_ORIGINAL_ID} />;
                case FrequentlyUsedField.SKU_ORIGINAL_ID:
                    return <SkuOriginalIdField key={FrequentlyUsedField.SKU_ORIGINAL_ID} />;
                case FrequentlyUsedField.QUANTITY_RANGE:
                    return <ItemQuantityRangeField key={FrequentlyUsedField.QUANTITY_RANGE} />;
                case FrequentlyUsedField.QUICK_CRITERION:
                    return <QuickField key={FrequentlyUsedField.QUICK_CRITERION} shopId={shopId} tab={tab} />;
                case FrequentlyUsedField.RETAILER:
                    return <RetailerField shopId={shopId} key={FrequentlyUsedField.RETAILER} />;
                case FrequentlyUsedField.SELLER_FLAG:
                    return <SellerFlagField key={FrequentlyUsedField.SELLER_FLAG} shop={shop} />;
                case FrequentlyUsedField.SELLER_REMARKS:
                    return <SellerRemarksField key={FrequentlyUsedField.SELLER_REMARKS} />;
                case FrequentlyUsedField.SHIPPING_ADDRESS:
                    return <ShippingAreaSelect key={FrequentlyUsedField.SHIPPING_ADDRESS} />;
                case FrequentlyUsedField.SHIPPER:
                    return <ShipperField shop={shop} key={FrequentlyUsedField.SHIPPER} />;
                case FrequentlyUsedField.SKU_PROPERTIES:
                    return <SkuPropertiesField key={FrequentlyUsedField.SKU_PROPERTIES} shopId={shopId} />;
                case FrequentlyUsedField.STOCK_NO:
                    return <StockNo key={FrequentlyUsedField.STOCK_NO} />;
                case FrequentlyUsedField.SUPPLIER:
                    if (fulfillmentType === FulfillmentType.DROP_SHIPPING) {
                        return <SupplierField key={FrequentlyUsedField.SUPPLIER} shopId={shopId} />;
                    }

                    return null;
                case FrequentlyUsedField.UNDELIVERABLE_AREA:
                    return <UndeliverableAreaField key={FrequentlyUsedField.UNDELIVERABLE_AREA} />;
                case FrequentlyUsedField.WAYBILL_NO:
                    return <WaybillNoField key={FrequentlyUsedField.WAYBILL_NO} />;
                case FrequentlyUsedField.WEIGHT_RANGE:
                    return <WeightRangeField key={FrequentlyUsedField.WEIGHT_RANGE} />;
                case FrequentlyUsedField.ORDER_TAG:
                    return <OrderTagSelectField key={FrequentlyUsedField.ORDER_TAG} shopType={shop.type} />;
                case FrequentlyUsedField.EXCLUDED_ORDER_TAG:
                    return <OrderTagUnSelectField key={FrequentlyUsedField.EXCLUDED_ORDER_TAG} shopType={shop.type} />;
                case FrequentlyUsedField.PRINT_BATCH_NO:
                    return <PrintBatchNoField key={FrequentlyUsedField.PRINT_BATCH_NO} />;
                case FrequentlyUsedField.ITEM_STATUSES:
                    return <ItemStatusesField key={FrequentlyUsedField.ITEM_STATUSES} />;
                default:
                    return null;
            }
        });
    }

    function buildFieldContainer() {
        if ([OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab)) {
            return (
                <>
                    <LogisticsCompanyField />
                </>
            );
        }

        if (!isShowMoreOptions) {
            return (
                <>
                    {buildFields(frequentlyUsedFields)}
                    <Form.Item noStyle hidden>
                        {buildFields(unFrequentlyUsedFields)}
                    </Form.Item>
                    <HiddenFields />
                </>
            );
        }

        const allFields = [...frequentlyUsedFields, ...unFrequentlyUsedFields];

        return (
            <>
                {buildFields(allFields)}
                <HiddenFields />
            </>
        );
    }

    function handleFieldsChange() {
        orderManagementStore.table.setActiveCriteriaId(null);
    }

    return (
        <>
            <div className="order-search-pane">
                <div style={{ maxWidth: "1480px" }}>
                    <OrderSearchForm
                        layout="inline"
                        size="small"
                        form={form}
                        className={formCls}
                        initialValues={initialValues}
                        onFieldsChange={handleFieldsChange}
                        colon={false}
                    >
                        <LinkedProductSearchFieldsStoreContext.Provider value={linkedProductSearchFieldsStore}>
                            <SearchContext.Provider value={{ onPressEnter: handleSearch }}>
                                {buildFieldContainer()}
                            </SearchContext.Provider>
                        </LinkedProductSearchFieldsStoreContext.Provider>
                    </OrderSearchForm>
                    <div className="mr50 flex justify-between mt5 ml5">
                        {![OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab) ? (
                            <FrequentlyUsedOrderSearchPanel shopId={shopId} />
                        ) : null}

                        <div>
                            {![OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab) ? (
                                <Button
                                    type="link"
                                    className="fs12 mr10"
                                    loading={orderSearchPanelLoading}
                                    onClick={() => {
                                        openModal(OrderSearchPaneSettingModal);
                                    }}
                                >
                                    搜索项编辑排序
                                </Button>
                            ) : null}

                            <SearchButton onSearch={handleSearch} />

                            <Button onClick={handleReset} size="small" className="mr10">
                                重置
                            </Button>

                            <OptionButton isShowMoreOptions={isShowMoreOptions} tab={tab} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default observer(SearchPanel);
